import { useRef, useState, type ReactNode } from 'react';
import type { TopUpRecord } from '@mypoz/shared';
import { clampPackagePage, packagePageCount } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { Pager } from '@/components/Pager.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';
import { TopUpStatusChip } from './TopUpStatusChip.js';

/** The same page size as the Notifications list. */
const TOP_UPS_PER_PAGE = 20;

export interface TopUpHistoryCardProps {
  status: 'loading' | 'ready' | 'error';
  records: readonly TopUpRecord[];
  onRetry: () => void;
  /**
   * Drawn in place of the card's own "Top-up history" heading — the Wallet page passes the
   * 账户明细 heading and tabs it shares with the transaction history (MYP-146).
   */
  header?: ReactNode;
}

/**
 * The Wallet page's top-up history (MYP-57), real since MYP-107 (`get_trade`, top-ups only).
 *
 * Every attempt is listed, unfinished ones included, labelled "Not completed" — on the test
 * account that is 492 of 553, so the list is paged in the browser, 20 to a page, with the same
 * pager as Packages and Notifications. The amount is shown with a plus: a top-up only ever adds,
 * and an unfinished one would have added that much had it been paid.
 */
export function TopUpHistoryCard({ status, records, onRetry, header }: TopUpHistoryCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [requestedPage, setRequestedPage] = useState(1);
  const top = useRef<HTMLDivElement>(null);

  /** Next page, then back to the list's own top: the pager sits under twenty rows. */
  const goToPage = (next: number) => {
    setRequestedPage(next);
    top.current?.scrollIntoView({ block: 'start' });
  };

  const pageCount = packagePageCount(records.length, TOP_UPS_PER_PAGE);
  const page = clampPackagePage(requestedPage, records.length, TOP_UPS_PER_PAGE);
  const pageStart = (page - 1) * TOP_UPS_PER_PAGE;
  const visible = records.slice(pageStart, pageStart + TOP_UPS_PER_PAGE);

  return (
    <div ref={top} className="grid scroll-mt-24 gap-4">
      <Card className="p-0">
        {header ?? (
          <div className="border-b border-line px-5 py-4 sm:px-6">
            <h2 className="text-base font-semibold text-ink">{t('wallet.history.title')}</h2>
          </div>
        )}

        {status === 'loading' ? (
          <p aria-busy="true" className="px-5 py-10 text-center text-sm text-muted sm:px-6">
            {t('wallet.history.loading')}
          </p>
        ) : status === 'error' ? (
          <div className="grid justify-items-center gap-3 px-5 py-10 sm:px-6">
            <p className="text-center text-sm text-ink">{t('wallet.history.failed')}</p>
            <button type="button" onClick={onRetry} className={buttonClass('secondary')}>
              <Icon name="refresh" size={18} />
              {t('actions.retry')}
            </button>
          </div>
        ) : records.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted sm:px-6">
            {t('wallet.history.empty')}
          </p>
        ) : (
          <div className="divide-y divide-line">
            {visible.map((record) => (
              <div
                key={record.id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 sm:px-6"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">
                    {record.methodKind !== undefined
                      ? t(`wallet.history.method.${record.methodKind}`)
                      : (record.method ?? t('wallet.history.unknownMethod'))}
                  </p>
                  <p className="truncate text-xs text-muted-soft tabular-nums">
                    {record.reference}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {record.dateIso ? (
                    <span className="text-sm text-muted tabular-nums">
                      {fmt.date(record.dateIso)}
                    </span>
                  ) : null}
                  <TopUpStatusChip status={record.status} />
                  <span className="text-sm font-semibold text-ink tabular-nums">
                    {'+ '}
                    {fmt.money(record.amountCents, record.currency)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {status === 'ready' && records.length > TOP_UPS_PER_PAGE ? (
        <Pager
          page={page}
          pageCount={pageCount}
          rangeStart={pageStart + 1}
          rangeEnd={pageStart + visible.length}
          total={records.length}
          onGo={goToPage}
          label={t('wallet.history.pagerLabel')}
        />
      ) : null}
    </div>
  );
}
